import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { FitText } from '@/components/common/FitText';
import { flagUrl } from '@/utils/flags';

export type PlayerCardState =
  | 'upcoming'
  | 'reveal'
  | 'choose'
  | 'expired'
  | 'revealed-common'
  | 'revealed-rare';

export type PlayerCardProps = {
  state?: PlayerCardState;
  /** Status line along the bottom of the card. Defaults per state. */
  label?: string;
  /** Revealed faces only. */
  firstName?: string;
  lastName?: string;
  shirtNumber?: number | null;
  /** Opta nationality name; the flag is looked up from it. */
  nationality?: string | null;
  /** Already translated position label and the club's display name. */
  position?: string;
  club?: string;
  /** Bottom plate: fixture before kickoff, score after, e.g. "@Westerlo 18:30". */
  plate?: string;
  /** PreFormatted tally line, e.g ""T 2 • I 0 • S 3 • G 1". */
  stats?: string;
  /** Shows the LIVE pill; the caller decides from the player's fixture status */
  live?: boolean;
  /** Headshot shown in the inner box on the revealed faces. */
  imageUrl?: string | null;
  onClick?: () => void;
  // Overrides the state's border color to mark the selected card
  borderColor?: string;
};

const UNREVEALED_BACKGROUND = 'radial-gradient(50% 50% at 50% 50%, #37424A 0%, #242529 100%)';
const COMMON_FACE = 'url(/card-common.png) center / 100% 100% no-repeat';
const GOLD_FACE = 'url(/card-gold.png) center / 100% 100% no-repeat';
const REVEAL_BACKGROUND = '#E02125';
const CHOOSE_BACKGROUND = '#3CBE1E';
const CHOOSE_INNER = 'radial-gradient(50% 50% at 50% 50%, #14520B 0%, #0D3A07 100%)';

// The art carries its own edge; only a caller's borderColor draws one on the revealed faces.
const BORDER_COLORS: Partial<Record<PlayerCardState, string>> = {};

const REVEAL_BORDER = {
  padding: 2,
  background: '#E02125',
  WebkitMask: 'linear-gradient(#000 0 0) content-box, linear-gradient(#000 0 0)',
  WebkitMaskComposite: 'xor',
  mask: 'linear-gradient(#000 0 0) content-box, linear-gradient(#000 0 0)',
  maskComposite: 'exclude',
} as const;

/*
  Every offset inside the card is tuned to the intrinsic 110×148 box, so callers
  resize it by scaling rather than by overriding widths — set `--player-card-scale`
  on any ancestor and the whole card, text and art included, scales in proportion.
  The scale lives on a wrapper so the card keeps its own `active:scale-95` press
  transform (an inline transform on the card itself would override that class).
*/
const CARD_SCALE = 'var(--player-card-scale, 1)';

/** Inner content box on the revealed faces, 6px down from the card's top edge. */
const INNER_BOX = 'absolute top-[6px] left-1/2 -translate-x-1/2 w-[96px] h-[86px] rounded-[5px]';

const PACK_WIDTH = 89.7;
const PACK_TOP = -28.5;

const CARD_FONT = "'Futura PT', Jost, sans-serif";
const TEXT_COLOR = '#000000';

/* Flag and shirt number, top-left over the photo box. */
const BADGE = { top: 8, left: 8 } as const;
const FLAG_SIZE = 15;
const NUMBER_FONT = 6;

/*
  Name block under the photo box, on the art's left margin. The surname shrinks
  to fit; the floor is where it stops reading on a 110px card.
*/
const NAME_BLOCK = { top: 96, left: 8, right: 8 } as const;
const FIRST_NAME_FONT = 6.5;
const LAST_NAME_MAX_FONT = 11;
const LAST_NAME_MIN_FONT = 8;
const META_FONT = 6.5;
const META_GAP = 3;

/* The slanted plate in the art, 131 to 142px down; the fixture or score sits on it. */
const PLATE = { top: 131, left: 15, width: 80, height: 11 } as const;
const PLATE_MAX_FONT = 7.5;
const PLATE_MIN_FONT = 6;

/* Type colour per face: white on the dark common art, black on gold. */
const FACE_TEXT: Partial<Record<PlayerCardState, string>> = {
  'revealed-common': '#FFFFFF',
  'revealed-rare': TEXT_COLOR,
};

/*
  Tally string along the bottom of the inner box. Overlays the headshot,
  so it costs no vertical space on a 148px card.
*/
const STAT_STRIP = {
  height: 12,
  background: 'rgba(0, 0, 0, 0.6)',
  color: '#FFFFFF',
} as const;
const STAT_MAX_FONT = 8;
const STAT_MIN_FONT = 6;

/* Fixture-in-progress pill, bottom-right of the photo box, clear of the flag and the stat strip. */
const LIVE_PILL = {
  right: 4,
  bottom: STAT_STRIP.height + 3,
  height: 12,
  padding: '0 5px',
  borderRadius: 6,
  background: '#E02125',
  color: '#FFFFFF',
  fontWeight: 700,
  fontSize: 7,
  lineHeight: '12px',
  letterSpacing: 0.4,
} as const;

/*
  Call-to-action on the reveal card. Same 96px width as the inner box, so it
  centres on the 110px card. Carries its own top offset rather than sharing
  TEXT_STACK_TOP, so nudging it can't move the name plate on the revealed faces.
*/
const REVEAL_BUTTON_TOP = 98;
const REVEAL_BUTTON = {
  width: 96,
  height: 20,
  borderRadius: 15,
  border: '0.75px solid #FFFFFF',
  background: 'linear-gradient(180deg, #FFFFFF 0%, #E0E0E0 100%)',
  boxShadow: '0px 3px 3px 0px #00000059',
} as const;

/*
  Expiry countdown under the button. Derived from the button's own position and
  height rather than a standalone offset, so moving the button carries it along.
*/
const REVEAL_EXPIRY_GAP = 10;
const REVEAL_EXPIRY_TOP = REVEAL_BUTTON_TOP + REVEAL_BUTTON.height + REVEAL_EXPIRY_GAP;
const REVEAL_EXPIRY_COLOR = '#FFFFFF';

/*
  Figma warns that these gradients carry a rotation CSS can't express. The
  percentage-sized ellipse and position below are valid CSS and match the
  design's geometry; only the rotation is dropped, so a tilted ellipse in the
  source renders here as an upright one.
*/
const INNER_BACKGROUNDS: Partial<Record<PlayerCardState, string>> = {
  reveal: 'radial-gradient(50% 50% at 50% 50%, #6D0D0F 0%, #530A0C 100%)',
  choose: CHOOSE_INNER,
};
const LABEL_COLOR = '#898E9A';

const CARD_BACKGROUNDS: Record<PlayerCardState, string> = {
  upcoming: UNREVEALED_BACKGROUND,
  expired: UNREVEALED_BACKGROUND,
  reveal: REVEAL_BACKGROUND,
  choose: CHOOSE_BACKGROUND,
  'revealed-common': COMMON_FACE,
  'revealed-rare': GOLD_FACE,
};

// Countdowns come from the slot's own window; the other faces blank rather than
// assert a time the card has no data for.
const DEFAULT_LABEL_KEYS: Partial<Record<PlayerCardState, string>> = {
  expired: 'card.expired',
  choose: 'card.pickYourPlayer',
};

/*
  lock.svg ships with a baked-in #2F3F51 fill, which matches the locked tab
  colour in TopNav but disappears against the card. Drawn as a mask so the
  glyph takes the label colour instead of shipping a second recoloured asset.
*/
const LOCK_MASK = {
  WebkitMaskImage: 'url(/lock.svg)',
  maskImage: 'url(/lock.svg)',
  WebkitMaskSize: 'contain',
  maskSize: 'contain',
  WebkitMaskRepeat: 'no-repeat',
  maskRepeat: 'no-repeat',
} as const;

/*
  Headshot on the revealed faces. The backend derives the URL from the player's
  Opta id and emits it whether or not a file exists in blob storage, so a load
  failure swaps in the silhouette instead of the browser's broken-image glyph.
  Callers key this on `src` so a card that re-renders for a different player
  retries the new URL rather than staying stuck on the fallback.

  Its own clipping wrapper rather than `overflow-hidden` on the inner box: the
  reveal face's pack art deliberately overhangs that box.
*/
function Headshot({ src }: { src: string }) {
  // The silhouette shows until the real headshot has loaded, rather than
  // replacing it after an error. A missing or slow blob then degrades to the
  // silhouette instead of leaving an empty card mid-swipe.
  const [loaded, setLoaded] = useState(false);
  return (
    <div aria-hidden className="absolute inset-0 overflow-hidden rounded-[5px] pointer-events-none">
      {!loaded && (
        <img
          src="/person.svg"
          alt=""
          className="absolute inset-0 h-full w-full object-contain object-bottom"
        />
      )}
      <img
        src={src}
        alt=""
        onLoad={() => setLoaded(true)}
        onError={() => setLoaded(false)}
        className={`relative h-full w-full object-contain object-bottom ${loaded ? '' : 'invisible'}`}
      />
    </div>
  );
}

/**
 * Compact 110×148 player card. The revealed faces draw the card art with the
 * headshot, flag, number, name, position, club and the fixture plate on top.
 */
export default function PlayerCard({
  state = 'upcoming',
  label,
  firstName,
  lastName,
  shirtNumber,
  nationality,
  position,
  club,
  plate,
  stats,
  live,
  imageUrl,
  onClick,
  borderColor,
}: PlayerCardProps) {
  const { t } = useTranslation();
  const interactive = onClick !== undefined;
  // Three groupings, deliberately not one flag: `reveal` shares the inner box
  // with the revealed faces but carries no text, and shows none of the dashed
  // placeholder treatment that `upcoming` and `expired` do.
  const revealed = state === 'revealed-common' || state === 'revealed-rare';
  // Both pack faces share the card's structure and differ only in accent.
  const isPack = state === 'reveal' || state === 'choose';
  const hasInnerBox = revealed || isPack;
  const hasPlaceholderArt = state === 'upcoming' || state === 'expired';
  const defaultKey = DEFAULT_LABEL_KEYS[state];
  const labelText = label ?? (defaultKey ? t(defaultKey) : '');

  const flag = flagUrl(nationality);

  return (
    <div
      className="shrink-0"
      style={{
        width: `calc(110px * ${CARD_SCALE})`,
        height: `calc(148px * ${CARD_SCALE})`,
        transform: `scale(${CARD_SCALE})`,
        transformOrigin: 'top left',
      }}
    >
      <div
        className={`w-[110px] h-[148px] rounded-[10px] relative shrink-0 ${
          // The pack art deliberately overhangs the top edge, so this state can't
          // clip. Every other face keeps its children inside the rounded box.
          isPack ? '' : 'overflow-hidden'
        } ${interactive ? 'cursor-pointer transition-transform duration-150 active:scale-95' : ''}`}
        style={{ background: CARD_BACKGROUNDS[state] }}
        onClick={onClick}
        role={interactive ? 'button' : undefined}
        tabIndex={interactive ? 0 : undefined}
        onKeyDown={
          interactive
            ? e => {
                if (e.key === 'Enter' || e.key === ' ') {
                  e.preventDefault();
                  onClick?.();
                }
              }
            : undefined
        }
      >
        {/*
        Drawn as an inset overlay rather than a border on the card itself: a real
        CSS border would shift the positioning origin for every absolute child in
        by 2px, so the inner box would sit 8px from the card's top edge instead of
        the 6px the design calls for.
      */}
        {(borderColor ?? BORDER_COLORS[state]) && (
          <div
            aria-hidden
            className="absolute inset-0 rounded-[10px] border-2 pointer-events-none"
            style={{ borderColor: borderColor ?? BORDER_COLORS[state] }}
          />
        )}

        {isPack && (
          <div
            aria-hidden
            className="absolute inset-0 rounded-[10px] pointer-events-none"
            style={{
              ...REVEAL_BORDER,
              background: state === 'choose' ? CHOOSE_BACKGROUND : REVEAL_BACKGROUND,
            }}
          />
        )}

        {hasInnerBox && (
          <div className={INNER_BOX} style={{ background: INNER_BACKGROUNDS[state] }}>
            {revealed && imageUrl && <Headshot key={imageUrl} src={imageUrl} />}
            {revealed && stats && (
              <div
                className="absolute inset-x-0 bottom-0 flex items-center justify-center rounded-b-[5px] px-[4px] font-poppins"
                style={STAT_STRIP}
              >
                <FitText
                  text={stats}
                  maxFontSize={STAT_MAX_FONT}
                  minFontSize={STAT_MIN_FONT}
                  className="max-w-full"
                  style={{ fontWeight: 600, lineHeight: '100%', letterSpacing: 0.2 }}
                />
              </div>
            )}
            {revealed && live && (
              <span className="absolute font-poppins uppercase" style={LIVE_PILL}>
                Live
              </span>
            )}
            {isPack && (
              // max-w-none overrides preflight's `img { max-width: 100% }`, which
              // would otherwise clamp the pack to the 96px box and kill the overhang.
              <img
                src="/pack-3-cards.png"
                alt=""
                className="absolute left-1/2 -translate-x-1/2 -rotate-[4deg] z-10 max-w-none pointer-events-none"
                style={{ top: PACK_TOP, width: PACK_WIDTH }}
              />
            )}
          </div>
        )}

        {revealed && (flag || shirtNumber != null) && (
          <div className="absolute flex flex-col items-center" style={BADGE}>
            {flag && <img src={flag} alt="" style={{ width: FLAG_SIZE, height: FLAG_SIZE }} />}
            {shirtNumber != null && (
              <span
                style={{
                  fontFamily: CARD_FONT,
                  fontWeight: 700,
                  fontSize: NUMBER_FONT,
                  lineHeight: '100%',
                  marginTop: 3,
                  color: FACE_TEXT[state],
                }}
              >
                #{shirtNumber}
              </span>
            )}
          </div>
        )}

        {/*
        A div rather than a <button>: the card root already carries the click
        handler and role="button" when interactive, so nesting a real button
        would put one interactive element inside another. Left readable (not
        aria-hidden) so the pill becomes the root button's accessible
        name — it's the only label the card has.
      */}
        {isPack && (
          <div
            className="absolute left-1/2 -translate-x-1/2 flex items-center justify-center font-poppins text-center"
            style={{
              top: REVEAL_BUTTON_TOP,
              ...REVEAL_BUTTON,
              fontWeight: 600,
              fontSize: 9,
              lineHeight: '100%',
              letterSpacing: 0,
              color: TEXT_COLOR,
            }}
          >
            {state === 'choose' ? t('card.choosePlayer') : t('card.revealPlayer')}
          </div>
        )}

        {isPack && labelText && (
          <div
            className="absolute inset-x-0 font-poppins text-center"
            style={{
              top: REVEAL_EXPIRY_TOP,
              fontWeight: 500,
              fontSize: 8,
              lineHeight: '100%',
              letterSpacing: 0,
              color: REVEAL_EXPIRY_COLOR,
            }}
          >
            {labelText}
          </div>
        )}

        {revealed && (
          <div
            className="absolute flex flex-col"
            style={{ ...NAME_BLOCK, fontFamily: CARD_FONT, color: FACE_TEXT[state] }}
          >
            {firstName && (
              <FitText
                text={firstName.toUpperCase()}
                maxFontSize={FIRST_NAME_FONT}
                minFontSize={FIRST_NAME_FONT}
                className="max-w-full"
                style={{ fontWeight: 700, fontStyle: 'italic', lineHeight: '100%' }}
              />
            )}
            {lastName && (
              <FitText
                text={lastName.toUpperCase()}
                maxFontSize={LAST_NAME_MAX_FONT}
                minFontSize={LAST_NAME_MIN_FONT}
                className="max-w-full"
                style={{ fontWeight: 800, fontStyle: 'italic', lineHeight: '100%' }}
              />
            )}
            {(position || club) && (
              <div
                className="flex justify-between gap-[4px]"
                style={{
                  fontWeight: 500,
                  fontSize: META_FONT,
                  lineHeight: '100%',
                  marginTop: META_GAP,
                }}
              >
                <span className="truncate">{position}</span>
                <span className="truncate text-right">{club}</span>
              </div>
            )}
          </div>
        )}

        {revealed && plate && (
          <div
            className="absolute flex items-center justify-center text-center"
            style={{ ...PLATE, fontFamily: CARD_FONT, fontWeight: 500, color: TEXT_COLOR }}
          >
            <FitText
              text={plate}
              maxFontSize={PLATE_MAX_FONT}
              minFontSize={PLATE_MIN_FONT}
              className="max-w-full"
              style={{ lineHeight: '100%' }}
            />
          </div>
        )}

        {/*
        The dashed border and silhouette belong to the unrevealed faces only.
        person.svg is a black-to-transparent gradient that would read as a hard
        shape against either light background.

        Drawn as an SVG stroke rather than `border: dashed` — CSS has no control
        over dash length, so a 6/2 pattern is only reachable via stroke-dasharray.
        Inset by half the stroke width so the 2px line sits inside the 110×148 box,
        which puts the path radius at 9 for an outer corner radius of 10.
      */}
        {hasPlaceholderArt && (
          <>
            <svg
              className="absolute inset-0 pointer-events-none"
              width="110"
              height="148"
              viewBox="0 0 110 148"
              fill="none"
              aria-hidden
            >
              <rect
                x="1"
                y="1"
                width="108"
                height="146"
                rx="9"
                stroke="#898E9A"
                strokeWidth="2"
                strokeDasharray="6 2"
              />
            </svg>

            <img
              src="/person.svg"
              alt=""
              className="absolute left-1/2 top-[calc(50%-22px)] -translate-x-1/2 -translate-y-1/2 w-[106px]"
            />
          </>
        )}

        {/* Pack faces render their label under the button instead, above. */}
        {labelText && !isPack && (
          <div
            className="absolute inset-x-0 bottom-[10px] flex items-center justify-center gap-[4px] font-poppins text-center"
            style={{
              fontWeight: 600,
              fontSize: 10,
              lineHeight: '15px',
              letterSpacing: 0,
              color: LABEL_COLOR,
            }}
          >
            {state === 'expired' && (
              <span aria-hidden className="w-[8px] h-[8px] shrink-0 bg-current" style={LOCK_MASK} />
            )}
            {labelText}
          </div>
        )}
      </div>
    </div>
  );
}
